import { useState, useEffect, useCallback, useRef } from 'react';
import { request } from '../../shared/services/api';

interface ActionCenterBadgeProps {
  /**
   * Variante visual:
   * - 'inline' (default): badge al final de la fila del sidebar expandido.
   * - 'overlay': badge absoluto sobre el icono cuando el sidebar está colapsado.
   */
  variant?: 'inline' | 'overlay';
}

/**
 * Badge del contador del Centro de Acciones.
 *
 * 0615+: ahora suma `/sign/action-center/count` (legacy) + `/workflow/pendientes/count`
 * (workflow ADR-025) para que el badge sea consistente con la lista que se ve
 * dentro del Centro de Acciones (que mergea ambos endpoints en cliente).
 */
export function ActionCenterBadge({ variant = 'inline' }: ActionCenterBadgeProps) {
  const [count, setCount] = useState(0);
  const authFailedRef = useRef(false);

  const fetchCount = useCallback(async () => {
    if (authFailedRef.current) return;
    try {
      const [legacy, workflow] = await Promise.all([
        request<{ data: { count: number } }>('/sign/action-center/count').catch(() => ({
          data: { count: 0 },
        })),
        request<{ data: { count: number } }>('/workflow/pendientes/count').catch(() => ({
          data: { count: 0 },
        })),
      ]);
      setCount((legacy.data?.count ?? 0) + (workflow.data?.count ?? 0));
    } catch (err: unknown) {
      // Si 401, dejar de intentar — sesión no establecida
      if (err instanceof Error && err.message.includes('401')) {
        authFailedRef.current = true;
      }
    }
  }, []);

  useEffect(() => {
    authFailedRef.current = false;
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  if (count === 0) return null;

  if (variant === 'overlay') {
    // Posicionado absoluto sobre el icono cuando el sidebar está colapsado.
    return (
      <span
        style={{
          position: 'absolute',
          top: 4,
          right: 6,
          minWidth: 16,
          height: 16,
          borderRadius: 8,
          background: '#E53E3E',
          color: '#fff',
          fontSize: 10,
          fontWeight: 700,
          padding: '0 4px',
          lineHeight: 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'inherit',
          pointerEvents: 'none',
        }}
      >
        {count > 99 ? '99+' : count}
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        borderRadius: 9,
        background: '#E53E3E',
        color: '#fff',
        fontSize: 10,
        fontWeight: 700,
        padding: '0 4px',
        lineHeight: 1,
        marginLeft: 'auto',
        flexShrink: 0,
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}
